import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { HighlightPipe } from '@shared/pipes/highlight.pipe';
import { ArrowLeft, ChevronRight, LucideAngularModule } from 'lucide-angular';

/**
 * Overlay menu component for displaying search results or other content in a dropdown.
 * Supports keyboard navigation, filtering, and selection of items.
 */
@Component({
  selector: 'app-overlay-menu',
  imports: [
    NgClass,
    TranslatePipe,
    HighlightPipe,
    LucideAngularModule,
  ],
  templateUrl: './overlay-menu.component.html',
  styleUrl: './overlay-menu.component.css'
})
export class OverlayMenuComponent implements OnChanges {
  /**
   * Reference to the overlay menu wrapper element for DOM manipulation.
   */
  @ViewChild('overlayMenuWrapper') public overlayMenuWrapper!: ElementRef;

  /**
   * Closes the overlay menu when clicking outside of it.
   * @param event - The mouse click event.
   */
  @HostListener('document:click', ['$event'])
  public clickOutside(event: Event): void {
    if (event instanceof MouseEvent && this.visible) {
      const target = event.target as HTMLElement;

      if (!this.overlayMenuWrapper?.nativeElement.contains(event.target) && !target.classList.contains('search-input')) {
        this.close();
      }
    }
  }

  /**
   * Handles keyboard navigation within the overlay menu.
   * Supports arrow keys for navigation, Enter for selection, and Escape to close.
   * @param event - The keyboard event.
   */
  @HostListener('window:keydown', ['$event'])
  public arrowHandler(event: Event): void {
    if (!(event instanceof KeyboardEvent) || !this.visible || event.target instanceof HTMLButtonElement) {
      return;
    }

    const handlers: Record<string, () => void> = {
      'ArrowDown': () => this.handleArrowDown(event),
      'ArrowUp': () => this.handleArrowUp(event),
      'Enter': () => this.handleEnter(event),
      'Escape': () => this.handleEscape()
    };

    const handler = handlers[event.key];
    if (handler) {
      handler();
    }
  }

  private handleArrowDown(event: KeyboardEvent): void {
    event.preventDefault();

    if (this.focusItem < this.flattenedResults.length - 1) {
      this.moveToNextSelectableItem();
    } else {
      this.wrapToFirstSelectableItem();
    }

    this.focusMenuItem();
  }

  private handleArrowUp(event: KeyboardEvent): void {
    event.preventDefault();

    if (this.focusItem > 0) {
      this.moveToPreviousSelectableItem();
    } else {
      this.wrapToLastSelectableItem();
    }

    this.focusMenuItem();
  }

  private handleEnter(event: KeyboardEvent): void {
    if (this.focusItem >= 0) {
      event.preventDefault();
      this.emitClick(this.flattenedResults[this.focusItem]);
    }
  }

  private handleEscape(): void {
    this.close();
  }

  private moveToNextSelectableItem(): void {
    this.focusItem += 1;

    // Skip category items
    if (this.isCurrentItemCategory() && this.focusItem < this.flattenedResults.length - 1) {
      this.focusItem += 1;
    }
  }

  private moveToPreviousSelectableItem(): void {
    this.focusItem -= 1;

    // Skip category items
    if (this.isCurrentItemCategory() && this.focusItem > 0) {
      this.focusItem -= 1;
    }
  }

  private wrapToFirstSelectableItem(): void {
    this.focusItem = this.findFirstSelectableItemIndex();
  }

  private wrapToLastSelectableItem(): void {
    this.focusItem = this.findLastSelectableItemIndex();
  }

  private isCurrentItemCategory(): boolean {
    return this.flattenedResults[this.focusItem]?.isCategory ?? false;
  }

  /**
   * Find the index of the first non-category item in the flattened results
   * @returns The index of the first selectable item
   */
  private findFirstSelectableItemIndex(): number {
    for (let i = 0; i < this.flattenedResults.length; i++) {
      if (!this.flattenedResults[i].isCategory) {
        return i;
      }
    }
    return 0; // Fallback to first item if all are categories
  }

  /**
   * Find the index of the last non-category item in the flattened results
   * @returns The index of the last selectable item
   */
  private findLastSelectableItemIndex(): number {
    for (let i = this.flattenedResults.length - 1; i >= 0; i--) {
      if (!this.flattenedResults[i].isCategory) {
        return i;
      }
    }
    return this.flattenedResults.length - 1; // Fallback to last item if all are categories
  }

  /** Utility **/
  protected readonly Array = Array;
  protected readonly backIcon = ArrowLeft;
  protected readonly nextIcon = ChevronRight;

  /**
   * Flattened version of search results for easier navigation.
   * Contains both category headers and individual result items.
   */
  public flattenedResults: any[] = [];

  /**
   * Controls the visibility of the overlay menu.
   * @default false
   */
  @Input() public visible: boolean = false;

  /**
   * The current search query text, used for highlighting matches.
   */
  @Input() public searchQuery: string = '';

  /**
   * Collection of search results to display in the overlay menu.
   * Results can optionally be grouped by categories.
   */
  @Input() public collection: SearchResultCollection[] = [];
  @Input() public loading = false;
  @Input() public loadFailed = false;
  @Input() public loadNotFound = false;
  @Input() public canGoBack = false;
  @Output() public goBack = new EventEmitter<void>();
  @Output() public retry = new EventEmitter<void>();

  public get hasResults(): boolean {
    return this.collection.some(collection => collection.results.length > 0);
  }

  /**
   * Event emitted when a result item is clicked or selected via keyboard.
   * Emits the selected item data.
   */
  @Output() public elementClicked: EventEmitter<any> = new EventEmitter();

  /**
   * Event emitted when an item is focused via keyboard navigation.
   * Emits the focused item data.
   */
  @Output() public focusedElement: EventEmitter<number> = new EventEmitter();

  /**
   * Index of the currently focused item in the flattenedResults array.
   * -1 indicates no item is focused.
   */
  public focusItem: number = -1;

  /**
   * Lifecycle hook that runs when input properties change.
   * Processes the collection data when it changes.
   * @param changes - The SimpleChanges object containing current and previous values.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['collection'] || changes['canGoBack']) {
      this.focusItem = -1;
      this.focusedElement.emit(this.focusItem);
    }
    this.flattenResults();
  }

  /**
   * Focuses the currently selected menu item and scrolls it into view.
   * Used during keyboard navigation.
   */
  public focusMenuItem(): void {
    if (this.focusItem < 0 || !this.overlayMenuWrapper) {
      return;
    }

    // Get all non-category menu items
    const items = this.overlayMenuWrapper.nativeElement.querySelectorAll('a');

    // Find the corresponding DOM element for the focused item
    let actualItemIndex = 0;
    let targetItem: HTMLElement | null = null;

    for (let i = 0; i < this.flattenedResults.length; i++) {
      if (!this.flattenedResults[i].isCategory) {
        if (i === this.focusItem) {
          targetItem = items[actualItemIndex] as HTMLElement;
          break;
        }
        actualItemIndex++;
      }
    }

    this.focusedElement.emit(this.focusItem);

    // Scroll the item into view if found
    if (targetItem) {
      targetItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /**
   * Flattens the nested collection structure into a single array for easier navigation.
   * Adds metadata to each item to track its original position and category.
   */
  private flattenResults(): void {
    this.flattenedResults = [];

    if (this.canGoBack) {
      this.flattenedResults.push({ isBack: true, isCategory: false });
    }

    this.collection.forEach((collection, categoryIndex) => {
      if (collection.category) {
        this.flattenedResults.push({
          id: `category-${categoryIndex}`,
          title: collection.category,
          originalIndex: -1,
          categoryIndex: categoryIndex,
          isCategory: true,
          category: collection.category
        });
      }

      // Add results
      collection.results.forEach((result, index) => {
        this.flattenedResults.push({
          id: result.id ?? `result-${categoryIndex}-${index}`,
          title: result.title ?? '',
          icon: result.icon,
          iconLabel: result.iconLabel,
          childrenLabel: result.childrenLabel,
          hasChildren: !!result.children,
          originalIndex: index,
          categoryIndex: categoryIndex,
          isGrouped: !!collection.category,
          isCategory: false
        });
      });
    });
  }

  /**
   * Handles click events on menu items and emits the selected item.
   * Ignores clicks on category headers.
   * @param item - The clicked item data.
   */
  public emitClick(item: any): void {
    if (!item || item.isCategory) {
      // Don't emit clicks on category headers
      return;
    }

    // Close before emitting so a drill-down selection can immediately reopen the menu.
    this.close();
    if (item.isBack) {
      this.goBack.emit();
      return;
    }

    // Find the original item from the collection
    if (item.categoryIndex >= 0 && item.categoryIndex < this.collection.length) {
      const category = this.collection[item.categoryIndex];

      if (category && item.originalIndex >= 0 && item.originalIndex < category.results.length) {
        const originalItem = category.results[item.originalIndex];

        // Emit the original item with additional metadata
        this.elementClicked.emit(originalItem);
      }
    } else {
      // Fallback to emitting the flattened item if we can't find the original
      this.elementClicked.emit(item);
    }

  }

  /**
   * Opens the overlay menu.
   * Uses setTimeout to avoid potential race conditions.
   */
  public open(): void {
    // Deferred to avoid race conditions
    setTimeout(() => {
      this.visible = true;
    }, 50);
  }

  /**
   * Closes the overlay menu and resets the focus state.
   */
  public close(): void {
    this.visible = false;
    this.focusItem = -1;
    this.focusedElement.emit(this.focusItem);
  }

}
